/**
 * Provider for the single-order data accumulator context.
 * @module dataAccumulator/DataAccumulatorProvider
 */
import React, { useCallback } from "react";
import { buildContextValue } from "./buildContextValue";
import { DataAccumulatorContext } from "./DataAccumulatorContext";
import { useFilteredSiblingOrders } from "./useFilteredSiblingOrders";
import { useSiblingOrderSelection } from "./useSiblingOrderSelection";
import { useSingleOrderDomainFetches } from "./useSingleOrderDomainFetches";

/**
 * Provider that holds parallel Postgres data-fetch instances for a single order.
 *
 * No data is fetched automatically — consumers call `fetchAll(orderId, orgId)`
 * to trigger all domains in parallel, and `abortAll()` to cancel every
 * in-flight request.
 *
 * @param props.children - React subtree to wrap
 */
export const DataAccumulatorForSingleOrderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    domains,
    denialQueryBucket,
    candidHistoryWriteback,
    fetchAll,
    abortAll,
    resetAll: resetDomainFetches,
    loading,
  } = useSingleOrderDomainFetches();

  const { filteredSiblingOrders, recomputeFilteredSiblingOrders } =
    useFilteredSiblingOrders({
      parentOrder: domains.generalOrderDetails.data?.order,
      parentOrderCoverageDetails: domains.coverageDetails.data,
      authOnFile: domains.authOnFileDetails.data?.auth_on_file,
      siblings: domains.siblingOrders.data?.siblings,
    });

  const {
    selectedSiblingOrderIds,
    setSelectedSiblingOrderIds,
    isSiblingOrderSelectionDisabled,
    setIsSiblingOrderSelectionDisabled,
  } = useSiblingOrderSelection(
    domains.generalOrderDetails.data?.order?.order_id,
  );

  const resetAll = useCallback(() => {
    resetDomainFetches();
    setSelectedSiblingOrderIds([]);
    setIsSiblingOrderSelectionDisabled(false);
  }, [
    resetDomainFetches,
    setSelectedSiblingOrderIds,
    setIsSiblingOrderSelectionDisabled,
  ]);

  const value = buildContextValue({
    domains,
    denialQueryBucket,
    candidHistoryWriteback,
    filteredSiblingOrders,
    recomputeFilteredSiblingOrders,
    loading,
    fetchAll,
    abortAll,
    resetAll,
    selectedSiblingOrderIds,
    setSelectedSiblingOrderIds,
    isSiblingOrderSelectionDisabled,
    setIsSiblingOrderSelectionDisabled,
  });

  return (
    <DataAccumulatorContext.Provider value={value}>
      {children}
    </DataAccumulatorContext.Provider>
  );
};
